/** Date Range Types */

export type DatePreset =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'custom';

export interface DateRange {
  from: Date | null;
  to: Date | null;
  preset: DatePreset;
}

/** Filter Types */

export interface DashboardFilters {
  searchQuery: string;
  dateRange: DateRange;
  sdrIds: string[];
  listTypes: ContactListType[];
  orgIds: string[];
}

export type ContactListType = 'CSV' | 'HUBSPOT' | 'CUSTOM_HUBSPOT' | 'OUTREACH' | 'SALESFORCE' | 'FOURMATIV';

/** Session Types */

export interface DashboardSession {
  id: string;
  shortId: string;
  sdrId: string;
  sdrName?: string;
  sdrProfilePicture?: string;
  startAtSession: string | { $date: string };
  updatedAtSession: string | { $date: string };
  listNames: string;
  type: ContactListType;
  totalContacts: number;
  organizationId: string;
  organizationName?: string;
  status: SessionStatus;
  totalCalls?: number;
  connectRate?: number;
  appointmentsSet?: number;
}

/** Metrics Types (calculated from sessions) */

export interface DashboardMetrics {
  totalSessions: number;
  totalContacts: number;
}

/** API Response Types */

export interface ApiError {
  message: string;
  raw?: string;
}

export type ApiResponse<T> =
  | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number }
    | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number; totalStatusCreated?: number; totalStatusSuccess?: number; totalStatusError?: number }
  | { success: false; error: ApiError };

export interface PaginatedResponse<T> {
  data: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
}

export interface PaginatedWithMetricsResponse<T> {
  data: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  totalStatusCreated: number;
  totalStatusSuccess: number;
  totalStatusError: number;
}

/** Session Details Types */

export type CallAction =
  | 'called'
  | 'transferred'
  | 'voicemail'
  | 'answered'
  | 'not_answered'
  | 'call_ended'
  | 'cut_off'
  | 'transfer_error';

export type CallDisposition =
  | 'refuse_transfer'
  | 'bad_number_busy_signal'
  | 'opt_out'
  | 'left_vm'
  | 'phone_refusal'
  | 'gatekeeper_conversation'
  | 'referred_another_contact'
  | 'no_longer_with_company'
  | 'set_appointment'
  | 'wrong_number'
  | 'phone_tree_no_connection'
  | 'no_answer'
  | 'decision_maker_conversation'
  | 'other'
  | 'sdr_did_not_pick_up'
  | 'transferred_without_disposition'
  | 'default_action'
  | 'TND'
  | 'DA'
  | 'LVM';

export interface SessionDetailsCall {
  action: CallAction;
  contactId: string;
  contactName: string;
  contactListId: string;
  contactListName: string;
  callId: string;
  disposition: CallDisposition | null;
  existRecordings: boolean;
  createdAt: string;
  agentId?: string;
  agentName?: string;
  flagged: boolean;
}

/** Contact Info (from /contacts/{id} endpoint) */

export interface ContactInfo {
  id: string;
  name: string;
  phone: string;
  email: string;
  jobTitle: string;
  accountName: string;
  notes?: string;
  notesSDR?: string;
  notesAgent?: string;
  crmId?: string;
  contactsListId: string;
  flagged?: boolean;
}

export enum SessionStatus {
  FINISHED = 'finished',
  PAUSED = 'paused',
  STARTED = 'started',
}

export interface SessionDetailsInfo {
  callSessionId: string;
  sdrId: string;
  sdrName: string;
  sdrEmail?: string;
  sdrProfilePicture?: string;
  organizationId: string;
  organizationName: string;
  status: SessionStatus;
  startAtSession: string;
  updatedAtSession: string;
  listType: ContactListType;
  totalContacts: number;
}

export interface SessionDetails {
  session: SessionDetailsInfo;
  calls: SessionDetailsCall[];
}

/** Session Stats (computed from calls) */

export interface SessionStats {
  // Call Flow Stats
  totalCalls: number;
  answered: number;
  transferred: number;

  // Dial to Connect
  dialToConnectRatio: number;

  // Disposition Stats
  gatekeeperConversation: number;
  leftVoicemail: number;
  wrongNumber: number;
  phoneTreeNoConnection: number;
  noLongerWithCompany: number;
  referredAnotherContact: number;
  decisionMakerConversation: number;
  optOut: number;
  noAnswer: number;
  badNumberBusySignal: number;
  phoneRefusal: number;
  refuseTransfer: number;

  // Key Metrics
  setAppointments: number;
  withRecording: number;
}

/** Recording Types */

export interface CallRecording {
  id: string;
  twilioRecordingId: string;
  recordingUrl: string;
  duration: string;
  label: 'SDR' | 'AGENT';
}

export interface SessionRecordings {
  sdrCallRecording?: CallRecording;
  agentCallRecording?: CallRecording;
}

/** SDR Info (for name lookup) */

export interface SdrInfo {
  id: string;
  name: string;
  email?: string;
}

/** Organization Info (for name lookup) */

export interface OrganizationInfo {
  id: string;
  name: string;
}

/** Backend Metrics Types (from /metrics endpoints) */

export interface BuildMetrics {
  called: number;
  answered: number;
  transferred: number;
  voicemail: number;
  not_answered: number;
  cut_off: number;
  transfer_error: number;
  call_ended: number;
}

export interface DispositionMetrics {
  refuse_transfer: number;
  bad_number_busy_signal: number;
  opt_out: number;
  left_vm: number;
  phone_refusal: number;
  gatekeeper_conversation: number;
  referred_another_contact: number;
  no_longer_with_company: number;
  set_appointment: number;
  wrong_number: number;
  phone_tree_no_connection: number;
  no_answer: number;
  decision_maker_conversation: number;
  other: number;
  sdr_did_not_pick_up: number;
  default_action: number;
  transferred_without_disposition: number;
}

export interface CallSyncError {
  callLogId: string;
  contactId?: string | null;
  contactName?: string | null;
  status: 'FAILED' | 'SKIPPED';
  reason?: string | null;
  crmRecordId?: string | null;
}

export interface SyncCounts {
  success: number;
  failed: number;
  skipped: number;
}

export interface IntegrationJob {
  _id: string;
  organizationId: string;
  contactListId: string;
  nonce: string;
  attempt: number;
  status: string;
  createdAt: {
    $date: string;
  } | string;
  updatedAt: {
    $date: string;
  } | string;
  logs: [];
  syncErrors?: CallSyncError[];
  counts?: SyncCounts;
  processedTotal?: number | null;
}

export interface Job {
  _id: string;
  organizationId: string;
  contactListId: string;
  nonce: string;
  attempt: number;
  status: string;
  createdAt: string;
  deleted: null;
  updatedAt: string;
}

export enum JobStatus {
  CREATED = 'CREATED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  PROCESSING = 'PROCESSING',
  CONFIRMED = 'CONFIRMED',
}

/** UI State Types */

export type LoadingState = 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR';

/** Helper Functions */

export function getDateRangeFromPreset (preset: DatePreset): { from: Date; to: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
  case 'today':
    return { from: today, to: now };
  case 'yesterday': {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return { from: yesterday, to: today };
  }
  case 'last7days': {
    const last7 = new Date(today);
    last7.setDate(last7.getDate() - 7);
    return { from: last7, to: now };
  }
  case 'last30days': {
    const last30 = new Date(today);
    last30.setDate(last30.getDate() - 30);
    return { from: last30, to: now };
  }
  case 'thisMonth':
    return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
  default:
    return { from: today, to: now };
  }
}

export const DEFAULT_FILTERS: DashboardFilters = {
  searchQuery: '',
  dateRange: {
    from: null,
    to: null,
    preset: 'last7days',
  },
  sdrIds: [],
  listTypes: [],
  orgIds: [],
};
